import { useMemo, useState } from 'react';
import type { FieldProps } from './TextField';
import type { QuestionOption } from '../../types';
import { useFormField } from '../../hooks/useFormField';
import { FieldsetWrapper } from '../QuestionWrapper';

function normalizeOptions(options: QuestionOption[] = []): QuestionOption[] {
  return options.map((option) => {
    // Backend option values can arrive as numbers (e.g. integer-keyed PHP
    // option arrays). Coerce to a string so the controlled `checked`
    // comparison and the stored answer stay the same type.
    const value = String(option.value);

    return {
      ...option,
      value,
      label: option.label || value,
    };
  });
}

export function RadioListField({ fieldSchema, group }: FieldProps) {
  const field = useFormField(fieldSchema, group);
  const options = useMemo<QuestionOption[]>(
    () => normalizeOptions(fieldSchema.options),
    [fieldSchema.options]
  );
  const [optionsHelpOpen, setOptionsHelpOpen] = useState<Record<number, boolean>>({});

  const toggleOptionHelp = (key: number) => {
    setOptionsHelpOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <FieldsetWrapper {...field} fieldSchema={fieldSchema} answered={field.hasAnswered()}>
      <div className="answer-group big-buttons vertical">
        {options.map((option, key) => (
          <div key={option.value}>
            <div className="table">
              <div className="answer">
                <input
                  type="radio"
                  checked={String(field.answer) === option.value}
                  onChange={() => field.setAnswer(option.value)}
                  name={field.getQuestionElementId()}
                  id={`${field.getQuestionElementId()}-${key}`}
                />
                <label htmlFor={`${field.getQuestionElementId()}-${key}`}>{option.label}</label>
              </div>
              {option.helpText && (
                <div className="help-toggle-wrapper">
                  <button
                    type="button"
                    onClick={() => toggleOptionHelp(key)}
                    aria-controls={`help-text-${field.getQuestionElementId()}-${key}`}
                  >
                    ?<span className="visuallyhidden">Show/Hide supporting help text</span>
                  </button>
                </div>
              )}
            </div>

            {option.helpText && (
              <div
                className={`a-help qa-help-text pull-right${optionsHelpOpen[key] ? ' open' : ''}`}
                id={`help-text-${field.getQuestionElementId()}-${key}`}
              >
                <div className="outer">
                  <span className="close" onClick={() => toggleOptionHelp(key)}>
                    ✕
                  </span>
                  <div className="inner" dangerouslySetInnerHTML={{ __html: option.helpText }} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </FieldsetWrapper>
  );
}
